/**
 * @fileoverview Routing par hash pour les vues principales.
 * Maintient une bijection entre `location.hash` et `viewState.filter` :
 *   #/all    ↔ filter='all'
 *   #/loans  ↔ filter='loaned'
 * Bénéfices : back/forward du navigateur, partage de lien interne,
 * restauration de la vue au reload sur une URL précise.
 */

import * as viewState from './view-state.js';

/**
 * Mapping URL → mise à jour partielle du viewState.
 * Les hashes de collection fixent section + filter ; #/wishlist fixe uniquement section.
 */
const HASH_TO_STATE = {
    '#/all':      { section: 'collection', filter: 'all' },
    '#/loans':    { section: 'collection', filter: 'loaned' },
    '#/wishlist': { section: 'wishlist' },
};

/** Mapping filtre → hash, utilisé quand section === 'collection'. */
const FILTER_TO_HASH = {
    'all':    '#/all',
    'loaned': '#/loans',
};

/**
 * Initialise le routing par hash.
 *
 * Au démarrage :
 *  - Si l'URL contient un hash valide → il prime sur le viewState (cas du lien partagé / reload sur vue).
 *  - Sinon → on aligne l'URL sur le viewState courant via `replaceState`
 *    (pas d'entrée d'historique pour cette synchro silencieuse).
 *
 * Ensuite, tout changement de filter pousse une nouvelle entrée d'historique
 * (`location.hash = ...`) pour que back/forward du navigateur navigue entre vues.
 */
export function initRouter() {
    if (HASH_TO_STATE[location.hash]) {
        // L'URL pilote l'état initial
        _applyHashToState();
    } else {
        // Aligner silencieusement l'URL sur le viewState (issu de localStorage)
        const state = viewState.get();
        const target = state.section === 'wishlist'
            ? '#/wishlist'
            : (FILTER_TO_HASH[state.filter] ?? '#/all');
        history.replaceState(null, '', target);
    }

    // Back / forward du navigateur ou modification manuelle de l'URL
    window.addEventListener('hashchange', _applyHashToState);

    // Changements applicatifs du viewState → push dans l'historique
    viewState.subscribe(_applyStateToHash);
}

/**
 * Lit `location.hash` et applique la mise à jour partielle correspondante au viewState.
 * Aucun effet si le hash n'est pas reconnu.
 * @private
 */
function _applyHashToState() {
    const update = HASH_TO_STATE[location.hash];
    if (!update) return;

    const current = viewState.get();
    const changed = Object.keys(update).some(k => update[k] !== current[k]);
    if (changed) viewState.set(update);
}

/**
 * Met à jour `location.hash` en fonction de la section et du filtre courants.
 * Crée une nouvelle entrée d'historique pour permettre la navigation back/forward.
 * @private
 * @param {{ section: string, filter: string }} state
 */
function _applyStateToHash(state) {
    const target = state.section === 'wishlist'
        ? '#/wishlist'
        : (FILTER_TO_HASH[state.filter] ?? '#/all');

    if (location.hash === target) return;
    location.hash = target;
}
